import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Shield, Lock, ArrowRight, Sparkles, Heart, Users, FileText,
  CheckCircle, AlertCircle, Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pin, setPin] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    const urlPin = searchParams.get("pin");
    const clientId = searchParams.get("client");
    if (urlPin && urlPin.length === 4) {
      handleAutoLogin(urlPin, clientId);
    }
  }, []);

  const handleAutoLogin = async (urlPin, clientId) => {
    setPin(urlPin.split(""));
    setLoading(true);
    try {
      const body = { pin: urlPin };
      if (clientId) body.client_id = clientId;
      const res = await fetch(`${API}/api/client/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem("client_token", data.token);
        localStorage.setItem("client_info", JSON.stringify(data.client));
        navigate("/client/dashboard");
      } else {
        setError(data.detail || "Invalid PIN");
        setPin(["", "", "", ""]);
      }
    } catch { setError("Connection error"); }
    finally { setLoading(false); }
  };

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value.slice(-1);
    setPin(newPin);
    setError("");
    if (value && index < 3) inputRefs[index + 1].current?.focus();
    if (newPin.every(d => d !== "")) handleLogin(newPin.join(""));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleLogin = async (pinStr) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/client/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: pinStr })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem("client_token", data.token);
        localStorage.setItem("client_info", JSON.stringify(data.client));
        navigate("/client/dashboard");
      } else if (data.multiple) {
        setError("Multiple accounts found. Please use the link shared by your advisor.");
        setPin(["", "", "", ""]);
      } else {
        setError(data.detail || "Invalid PIN. Please check with your advisor.");
        setPin(["", "", "", ""]);
        setTimeout(() => inputRefs[0].current?.focus(), 100);
      }
    } catch { setError("Connection error. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#E7EDF6] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] mb-4 shadow-lg shadow-[#D4A017]/15">
            <Shield className="h-8 w-8 text-[#0D1A3A]" />
          </div>
          <h1 className="text-2xl font-bold text-[#1B2A4A]">My Insurance</h1>
          <p className="text-[#3A4557] text-sm mt-1">Enter your 4-digit PIN to view your policies</p>
        </div>

        {/* PIN Card */}
        <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-6 border border-[#C9D4E6] shadow-xl">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="h-4 w-4 text-[#A87608]" />
            <span className="text-[#3A4557] text-xs font-medium uppercase tracking-wider">4-Digit PIN</span>
          </div>

          <div className="flex justify-center gap-3 mb-5">
            {pin.map((digit, i) => (
              <input
                key={i}
                ref={inputRefs[i]}
                type="tel"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handlePinChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                disabled={loading}
                className="w-14 h-14 text-center text-2xl font-bold rounded-xl bg-[#EDF1F8] border-2 border-[#C9D4E6] text-[#1B2A4A] focus:border-[#D4A017] focus:ring-2 focus:ring-[#D4A017]/20 outline-none transition-all disabled:opacity-50"
                data-testid={`client-pin-${i}`}
              />
            ))}
          </div>

          {error && (
            <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2 mb-4">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-red-600 text-xs">{error}</p>
            </motion.div>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-2 py-2">
              <Loader2 className="h-5 w-5 text-[#A87608] animate-spin" />
              <span className="text-[#A87608] text-sm">Verifying...</span>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          {[
            { icon: Heart, label: "Family Coverage" },
            { icon: FileText, label: "Policy Details" },
            { icon: Sparkles, label: "AI Explainer" }
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="bg-[#EDF1F8]/30 rounded-xl p-3 text-center border border-[#C9D4E6]/50">
              <Icon className="h-5 w-5 text-[#A87608]/70 mx-auto mb-1" />
              <p className="text-[#3A4557] text-[10px] font-medium">{label}</p>
            </div>
          ))}
        </div>

        <p className="text-center text-[#3A4557] text-[10px] mt-6">
          PIN shared by your insurance advisor
        </p>
      </motion.div>
    </div>
  );
}
